import React, { useEffect, useState } from 'react';
import { Container, Grid, Typography, Box, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// FIREBASE IMPORTS
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from '../firebase';

import RecipeCard from '../components/RecipeCard';

const Favorites = () => {
  const [favoriteRecipes, setFavoriteRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const normalizeRecipe = (data, id) => {
    let rawAuthor = data.authorName || data.username || "Chef";
    if (rawAuthor.includes("@")) {
      rawAuthor = rawAuthor.split("@")[0];
    }
    return {
      ...data,
      id: String(id),
      recipe_name: data.title || data.recipe_name || "Untitled",
      username: rawAuthor,
      image: (data.images && data.images.length > 0) ? data.images : (data.image || "/no-image.png"),
      prep_time: data.prepTime || data.prep_time || 0,
      cook_time: data.cookTime || data.cook_time || 0,
      is_favorited: true // It's on the favorites page, so we know it's favorited!
    };
  };

  useEffect(() => {
    const auth = getAuth();
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) { 
          setIsLoading(false); 
          navigate('/login');
          return; 
      }

      try {
        // 1. Get the user's document to find their saved IDs
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        let favoriteIds = [];
        if (userDocSnap.exists()) {
            favoriteIds = userDocSnap.data().favoriteIds || [];
        }

        // If they have no favorites, stop here
        if (favoriteIds.length === 0) {
            setFavoriteRecipes([]);
            setIsLoading(false);
            return;
        }

        // 2. Fetch full details for each ID in the array
        const fullRecipePromises = favoriteIds.map(async (recipeId) => {
            try {
                const recipeDoc = await getDoc(doc(db, "recipes", String(recipeId)));
                if (recipeDoc.exists()) {
                    return normalizeRecipe(recipeDoc.data(), recipeDoc.id);
                }
                return null; // Recipe might have been deleted by the author
            } catch (err) {
                console.warn(`Could not load recipe ${recipeId}`);
                return null; 
            }
        });

        const fullRecipes = await Promise.all(fullRecipePromises);
        
        // Filter out any nulls (deleted recipes)
        setFavoriteRecipes(fullRecipes.filter(r => r !== null));

      } catch (err) {
        console.error("Error loading favorites:", err);
        setError("Failed to fetch favorite recipes.");
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (isLoading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Container sx={{ mt: 4, mb: 8 }}>
      <Typography variant="h4" gutterBottom sx={{ fontFamily: "'Patrick Hand', cursive" }}>My Favorite Recipes</Typography>
      
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      {favoriteRecipes.length === 0 ? (
        <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">You haven't favorited any recipes yet.</Typography>
            <Button variant="contained" onClick={() => navigate('/recipes')} sx={{ mt: 2 }}>Browse Recipes</Button>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {favoriteRecipes.map((recipe) => (
            <Grid item xs={12} sm={6} md={4} key={recipe.id} sx={{ display: 'flex' }}>
              <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <RecipeCard recipe={recipe} />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default Favorites;